import { useEffect } from "react";
import type { ForwardedRef } from "react";
import type { DropzoneOptions, FileWithPath } from "react-dropzone";
import { useDropzone } from "react-dropzone";
import { t } from "ttag";

import { MAX_UPLOAD_SIZE, SIZE } from "@/shared/models/legacy-constants";
import { bytesToMegabytes, formatBytes, forwardRef, tw } from "@/shared/utils";
import { icons, IconWrapper, useToastStore } from "../common";
import { Message } from "./message";

interface DropzoneProps extends DropzoneOptions {
  id?: string;
  compact?: boolean;
  error?: string | boolean;
  label?: string;
  message?: string;
  placeholder?: string;
  value?: FileWithPath | FileWithPath[] | null;
}

const sizeInBytes = formatBytes(MAX_UPLOAD_SIZE);

function maxSizeValidator(file: FileWithPath) {
  if (file.size > MAX_UPLOAD_SIZE) {
    return {
      code: "file-too-large",
      message: t`Oops! File too large (Max. ${sizeInBytes}). Please try reducing the file size before uploading again.`,
    };
  }

  return null;
}

export const Dropzone = forwardRef(
  (props: DropzoneProps, ref: ForwardedRef<HTMLInputElement>) => {
    const { pushToast } = useToastStore();
    const {
      value,
      id,
      compact = true,
      error,
      label = t`Drop your files here`,
      message,
      multiple = false,
      placeholder = t`Drag 'n' drop some files here, or click to select files`,
    } = props;

    const { getRootProps, getInputProps, fileRejections } = useDropzone({
      ...props,
      multiple,
      validator: maxSizeValidator,
    });

    const selectedFiles =
      (Array.isArray(value) ? value : value && [value]) ?? [];

    const files = selectedFiles.map((file: FileWithPath) => (
      <span key={file.path}>
        {file.path}
        {multiple && ` - ${bytesToMegabytes(file.size)} MB`}
      </span>
    ));

    const fileSize =
      selectedFiles[0]?.size && bytesToMegabytes(selectedFiles[0].size);

    useEffect(() => {
      fileRejections.forEach(
        ({ file, errors }) =>
          void pushToast({
            type: "error",
            title: file.name,
            message: errors.map((e) => e.message).join("\n"),
          }),
      );
    }, [fileRejections, pushToast]);

    return (
      <div className="flex grow flex-col gap-1.5 text-center wrap-anywhere">
        <div
          {...getRootProps({ ref })}
          className={tw(
            "focus:ring-opacity-70 flex grow flex-col items-center justify-center gap-4 rounded-2xl border border-dashed border-neutral-950 bg-white p-6 focus:ring-2 focus:ring-secondary-200 focus:outline-hidden",
            Boolean(files.length) && "bg-secondary-100",
          )}
        >
          <div>
            <label htmlFor={id} className="text-neutral-950">
              {files.length ? files : label}
            </label>
            <input {...getInputProps({ id })} />

            <p className="text-sm font-medium text-neutral-600">
              {fileSize ?? placeholder}
            </p>
          </div>

          {files.length ? (
            <IconWrapper
              size={SIZE.LARGE}
              className="rounded-full border-2 border-neutral-600 bg-white p-1.5 text-neutral-950"
            >
              <icons.Check />
            </IconWrapper>
          ) : (
            <IconWrapper
              size={SIZE.LARGE}
              className="rounded-lg border border-neutral-600 p-2 text-neutral-800"
            >
              <icons.Plus />
            </IconWrapper>
          )}
        </div>

        {(!compact || !!message || !!error) && (
          <Message message={message} error={error} />
        )}
      </div>
    );
  },
);
